import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiErrorMessage, launchApi } from '../api';
import { help } from '../helpTexts';
import { cardSx, chipButtonSx, launchColors, overlineSx } from '../launchTheme';
import { Assignee, ProductInfoFilter, ProductInfoList, ProductSummary } from '../types';
import { AssetHoverPreview } from '../ui/AssetHoverLink';
import AthenaMark from '../ui/AthenaMark';
import InfoTip from '../ui/InfoTip';
import KpiTile from '../ui/KpiTile';
import ListSkeleton from '../ui/ListSkeleton';
import SearchField from '../ui/SearchField';
import { StatusPill } from '../ui/StatusPill';
import ImportDialog from './ImportDialog';
import NewProductDialog from './NewProductDialog';
import ProductCard from './ProductCard';
import { QUICK_FILTERS, STATUS_OPTIONS, statusLabel, statusStyle } from './productLabels';

interface Props {
  canEdit: boolean;
  canImport: boolean;
  people?: Assignee[];
  openProductId?: string | null;
  onOpened?: () => void;
}

type Grouping = 'supplier' | 'group';
type Layout = 'list' | 'table';

const NO_PEOPLE: Assignee[] = [];
const EMPTY_FILTER: ProductInfoFilter = { q: '', list: '', group: '', supplier: '', status: '', quick: '' };

const groupKey = (product: ProductSummary, grouping: Grouping) => (grouping === 'supplier' ? product.supplier : product.group);
const emptyGroup = (grouping: Grouping) => (grouping === 'supplier' ? 'Utan leverantör' : 'Utan varugrupp');

export function groupProducts(products: ProductSummary[], grouping: Grouping): Array<{ name: string; products: ProductSummary[] }> {
  const groups: Array<{ name: string; products: ProductSummary[] }> = [];
  const byName = new Map<string, ProductSummary[]>();
  for (const product of products) {
    const name = groupKey(product, grouping) || emptyGroup(grouping);
    let items = byName.get(name);
    if (!items) {
      items = [];
      byName.set(name, items);
      groups.push({ name, products: items });
    }
    items.push(product);
  }
  const blank = emptyGroup(grouping);
  groups.sort((left, right) => (left.name === blank ? 1 : right.name === blank ? -1 : left.name.localeCompare(right.name, 'sv')));
  for (const group of groups) {
    group.products.sort((left, right) => left.articleNumber.localeCompare(right.articleNumber, 'sv', { numeric: true }));
  }
  return groups;
}

const moveFocus = (event: React.KeyboardEvent<HTMLElement>) => {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-product-row]'));
  const index = rows.indexOf(event.currentTarget);
  const next = event.key === 'ArrowDown' ? rows[index + 1]
    : event.key === 'ArrowUp' ? rows[index - 1]
      : event.key === 'Home' ? rows[0]
        : event.key === 'End' ? rows[rows.length - 1]
          : undefined;
  if (next && next !== event.currentTarget) {
    event.preventDefault();
    next.focus();
  }
};

const ProductRow = React.memo(function ProductRow({ product, selected, onSelect }: { product: ProductSummary; selected: boolean; onSelect: (id: string) => void }) {
  const pill = statusStyle(product.status);
  const ready = product.readiness.missing.length === 0;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderTop: `1px solid ${launchColors.rowDivider}`,
        bgcolor: selected ? launchColors.softNavy : 'transparent',
        '&:hover': { bgcolor: selected ? launchColors.softNavy : launchColors.section },
      }}
    >
      <ButtonBase
        data-product-row
        onClick={() => onSelect(product.id)}
        onKeyDown={moveFocus}
        aria-current={selected ? 'true' : undefined}
        aria-label={`${product.articleNumber} ${product.name || 'Namn saknas'}`}
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 0.75,
          textAlign: 'left',
          '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: -2 },
        }}
      >
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, minWidth: 64 }}>
          {product.athenaAssetId ? (
            <AssetHoverPreview assetId={product.athenaAssetId} title={product.name || product.articleNumber} hasThumbnail>{product.articleNumber}</AssetHoverPreview>
          ) : product.articleNumber}
        </Typography>
        <Typography component="span" sx={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: product.name ? launchColors.text : launchColors.textMuted }}>
          {product.name || 'Namn saknas'}
        </Typography>
        {product.warnings.length > 0 && <WarningAmberIcon titleAccess="Har varningar" sx={{ fontSize: 16, color: launchColors.warningMarker }} />}
        <Typography component="span" title={ready ? 'Klar för Dabas' : `Saknas: ${product.readiness.missing.join(', ')}`} sx={{ fontSize: 12, fontWeight: 700, color: ready ? launchColors.mossDark : launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
          {product.readiness.done}/{product.readiness.total}
        </Typography>
        <StatusPill label={statusLabel(product.status)} fg={pill.fg} bg={pill.bg} />
      </ButtonBase>
      {product.athenaAssetId && (
        <Box sx={{ pr: 1 }}>
          <AthenaMark assetId={product.athenaAssetId} articleNumber={product.articleNumber} />
        </Box>
      )}
    </Box>
  );
});

const ProductInfoView: React.FC<Props> = ({ canEdit, canImport, people = NO_PEOPLE, openProductId, onOpened }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const searchRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<ProductInfoFilter>(EMPTY_FILTER);
  const [query, setQuery] = useState('');
  const [data, setData] = useState<ProductInfoList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [grouping, setGrouping] = useState<Grouping>('supplier');
  const [layout, setLayout] = useState<Layout>('list');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const names = useMemo(() => Object.fromEntries(people.map((person) => [person.id, person.name])), [people]);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter((current) => (current.q === query ? current : { ...current, q: query })), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    launchApi.productInfo(filter)
      .then((next) => {
        if (cancelled) return;
        setData(next);
        setError(null);
      })
      .catch((requestError) => { if (!cancelled) setError(apiErrorMessage(requestError, 'Produktinformationen kunde inte hämtas.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter, refresh]);

  useEffect(() => {
    if (!openProductId) return;
    setSelectedId(openProductId);
    onOpened?.();
  }, [onOpened, openProductId]);

  const reload = useCallback(() => setRefresh((value) => value + 1), []);
  const groups = useMemo(() => groupProducts(data?.products ?? [], grouping), [data, grouping]);
  const facets = data?.facets ?? { lists: [], groups: [], suppliers: [] };
  const setField = (key: keyof ProductInfoFilter, value: string) => setFilter((current) => ({ ...current, [key]: value }));
  const filtered = Boolean(filter.q || filter.list || filter.group || filter.supplier || filter.status || filter.quick);

  const exportCsv = async () => {
    try {
      const blob = await launchApi.exportProductInfo(filter);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `produktinformation-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Exporten misslyckades.'));
    }
  };

  const toggleGroup = (name: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    return next;
  });

  const listPane = (
    <Box sx={[cardSx, { position: 'relative', overflow: 'hidden', opacity: loading && data ? 0.6 : 1 }]}>
      {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, height: 2 }} />}
      {!data ? <ListSkeleton /> : null}
      {!data ? null : layout === 'list' ? (
        <Box role="list" aria-label="Produkter">
          {groups.map((group) => {
            const open = !collapsed.has(group.name);
            return (
              <Box key={group.name} role="listitem">
                <ButtonBase
                  onClick={() => toggleGroup(group.name)}
                  aria-expanded={open}
                  sx={{ width: '100%', justifyContent: 'flex-start', gap: 0.75, px: 1, py: 0.75, bgcolor: launchColors.section, borderTop: `1px solid ${launchColors.divider}` }}
                >
                  {open ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                  <Typography component="span" sx={[overlineSx, { flex: 1, textAlign: 'left' }]}>{group.name}</Typography>
                  <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{group.products.length}</Typography>
                </ButtonBase>
                {open && group.products.map((product) => (
                  <ProductRow key={product.id} product={product} selected={product.id === selectedId} onSelect={setSelectedId} />
                ))}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Produkter i tabell">
            <TableHead>
              <TableRow>
                {['Artnr', 'Namn', 'Leverantör', 'Varugrupp', 'Lista', 'EAN', 'Klar', 'Status'].map((label) => <TableCell key={label} sx={{ fontWeight: 700 }}>{label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.products ?? []).map((product) => (
                <TableRow key={product.id} hover selected={product.id === selectedId} onClick={() => setSelectedId(product.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{product.articleNumber}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.supplier}</TableCell>
                  <TableCell>{product.group}</TableCell>
                  <TableCell>{product.list}</TableCell>
                  <TableCell>{product.eanConsumer}</TableCell>
                  <TableCell>{product.readiness.done}/{product.readiness.total}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{statusLabel(product.status)}</span>
                      {product.athenaAssetId && <AthenaMark assetId={product.athenaAssetId} articleNumber={product.articleNumber} />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {data && data.products.length === 0 && (
        <Typography sx={{ p: 2, fontSize: 13, color: launchColors.textSecondary }}>
          {data.total === 0 ? 'Ingen produktinformation ännu.' : 'Inga produkter matchar filtret.'}
        </Typography>
      )}
    </Box>
  );

  const card = selectedId && data ? (
    <ProductCard
      productId={selectedId}
      fields={data.fields}
      facets={facets}
      canEdit={canEdit}
      names={names}
      athenaAssetId={data.products.find((product) => product.id === selectedId)?.athenaAssetId}
      onChanged={reload}
      onBack={narrow ? () => setSelectedId(null) : undefined}
      onOpen={setSelectedId}
    />
  ) : (
    <Box sx={[cardSx, { p: 3, textAlign: 'center', color: launchColors.textSecondary }]}>
      <Typography sx={{ fontSize: 14 }}>Välj en produkt i listan.</Typography>
    </Box>
  );

  const quickCount = (value: string) => (data?.stats && value in data.stats ? data.stats[value as keyof NonNullable<typeof data.stats>] : null);
  const chooseQuick = (value: string) => setField('quick', filter.quick === value ? '' : value);

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, minHeight: 112 }}>
        {data?.stats && (
          <>
            <KpiTile value={data.stats.missing} label="Saknar uppgifter" fg={launchColors.warningText} bg={data.stats.missing ? launchColors.warningBg : launchColors.card} accent={launchColors.warningMarker} icon={<WarningAmberIcon />} info={help.productInfoMissing} hint="Visa dem" onClick={() => chooseQuick('missing')} />
            <KpiTile value={data.stats.new} label="Kommande" fg={launchColors.navy} bg={data.stats.new ? launchColors.softNavy : launchColors.card} accent={launchColors.navy} icon={<HourglassEmptyIcon />} info={help.productInfoComing} hint="Visa kommande" onClick={() => chooseQuick('new')} />
            <KpiTile value={data.stats.ready} label="Klar för Dabas" fg={launchColors.mossDark} bg={launchColors.card} accent={launchColors.moss} icon={<CheckCircleOutlineIcon />} info={help.productInfoReadyCount} hint="Visa klara" zeroText={null} onClick={() => chooseQuick('ready')} />
            <KpiTile value={data.stats.warnings} label="Varningar" fg={launchColors.dangerText} bg={data.stats.warnings ? launchColors.dangerBg : launchColors.card} accent={launchColors.dangerText} icon={<ErrorOutlineIcon />} info={help.productInfoWarnings} hint="Visa varningar" onClick={() => chooseQuick('warnings')} />
          </>
        )}
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchField
          label="Sök artikelnummer, EAN, namn eller leverantör"
          value={query}
          inputRef={searchRef}
          onChange={setQuery}
          sx={{ flex: '1 1 280px', minWidth: 0, bgcolor: launchColors.card }}
        />
        <TextField select size="small" label="Lista" value={filter.list} onChange={(event) => setField('list', event.target.value)} sx={{ flex: '0 0 150px', bgcolor: launchColors.card }}>
          <MenuItem value="">Alla listor</MenuItem>
          {facets.lists.map((item) => <MenuItem key={item.name} value={item.name}>{item.name} ({item.count})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Varugrupp" value={filter.group} onChange={(event) => setField('group', event.target.value)} sx={{ flex: '0 0 170px', bgcolor: launchColors.card }}>
          <MenuItem value="">Alla varugrupper</MenuItem>
          {facets.groups.map((item) => <MenuItem key={item.name} value={item.name}>{item.name} ({item.count})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Leverantör" value={filter.supplier} onChange={(event) => setField('supplier', event.target.value)} sx={{ flex: '0 0 190px', bgcolor: launchColors.card }}>
          <MenuItem value="">Alla leverantörer</MenuItem>
          {facets.suppliers.map((item) => <MenuItem key={item.name} value={item.name}>{item.name} ({item.count})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filter.status} onChange={(event) => setField('status', event.target.value)} sx={{ flex: '0 0 170px', bgcolor: launchColors.card }}>
          <MenuItem value="">Alla utom utgått</MenuItem>
          {STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </TextField>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto' }}>
          {canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} sx={{ whiteSpace: 'nowrap' }}>Ny produkt</Button>}
          <InfoTip text={help.productInfo} />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        {QUICK_FILTERS.map((item) => {
          const count = quickCount(item.value);
          return (
            <Button key={item.value} aria-pressed={filter.quick === item.value} onClick={() => chooseQuick(item.value)} sx={chipButtonSx(filter.quick === item.value)}>
              {item.label}{count === null ? '' : ` (${count})`}
            </Button>
          );
        })}
        <Button size="small" onClick={() => { setQuery(''); setFilter(EMPTY_FILTER); }} tabIndex={filtered ? 0 : -1} aria-hidden={!filtered} sx={{ visibility: filtered ? 'visible' : 'hidden' }}>Rensa filter</Button>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={grouping} onChange={(_event, value: Grouping | null) => { if (value) setGrouping(value); }} aria-label="Gruppera efter">
          <ToggleButton value="supplier">Leverantör</ToggleButton>
          <ToggleButton value="group">Varugrupp</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={layout} onChange={(_event, value: Layout | null) => { if (value) setLayout(value); }} aria-label="Visning">
          <ToggleButton value="list">Lista</ToggleButton>
          <ToggleButton value="table">Tabell</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={exportCsv} disabled={!data?.products.length}>CSV</Button>
        {canImport && <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>Läs in Excel</Button>}
      </Stack>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice || ''} />
      {data && (
        <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
          {data.products.length} av {data.total} produkter
        </Typography>
      )}
      {data && data.total === 0 && canImport && (
        <Alert severity="info" action={<Button onClick={() => setImportOpen(true)}>Läs in Excel</Button>}>
          Läs in Excel-filen Produktinformation Dabas för att komma igång.
        </Alert>
      )}
      {narrow ? (selectedId ? card : listPane) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: layout === 'table' ? 'minmax(0, 1.2fr) minmax(0, 1fr)' : 'minmax(320px, 0.8fr) minmax(0, 1.2fr)', gap: 2, alignItems: 'start' }}>
          <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto', borderRadius: '12px' }}>{listPane}</Box>
          <Box sx={{ position: 'sticky', top: 0 }}>{card}</Box>
        </Box>
      )}
      <NewProductDialog
        open={newOpen}
        facets={facets}
        defaults={{ list: filter.list || '', group: filter.group || '', supplier: filter.supplier || '' }}
        onClose={() => setNewOpen(false)}
        onCreated={(detail) => {
          setNewOpen(false);
          setSelectedId(detail.product.id);
          setNotice(`Produkten ${detail.product.articleNumber} skapades.`);
          reload();
        }}
      />
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(message) => {
          setImportOpen(false);
          setNotice(message);
          reload();
        }}
      />
    </Stack>
  );
};

export default ProductInfoView;
